import { Button, TextField } from '@warp-ds/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { Autocomplete } from 'shared/search/common/autocomplete/Autocomplete';
import { fhhAutocompleteMapper } from 'shared/search/common/autocomplete/utils/autocompleteMapper';
import { FrontpageDatePickerWrapper } from 'shared/search/common/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { generateGooglePlacesSessionToken } from 'shared/search/common/utils/googlePlacesSessionToken';
import { BedroomFilter } from 'shared/search/fhh/BedroomFilter';
import type { Filters } from '@/domain';
import { HiddenSearchInputs } from './components/HiddenSearchInputs';
import { SearchDetails } from './components/SearchDetails';
import { useSearchFormState } from './hooks/useSearchFormState';
import { formatDestinationText, formatGuestAndBedroomText, hasSearchParams } from './utils/searchUtils';

export const FhhSearch = ({ filters }: { filters: Filters }) => {
    const [isOpen, setIsOpen] = useState(() => !hasSearchParams(filters));
    const userInteractedRef = useRef(false);
    const { apiExternalUrl } = useConfig();
    const [googlePlacesSessionToken] = useState(generateGooglePlacesSessionToken);

    useEffect(() => {
        if (userInteractedRef.current) return;
        const shouldBeOpen = !hasSearchParams(filters);
        if (isOpen === shouldBeOpen) return;
        setIsOpen(shouldBeOpen);
    }, [filters, isOpen]);

    const { fromDate, setFromDate, toDate, setToDate, destination, setDestination, bedrooms, setBedrooms, beds, setBeds } =
        useSearchFormState(filters);

    const closeSearchForm = useCallback(() => {
        setIsOpen(false);
        userInteractedRef.current = true;
    }, []);
    const openSearchForm = useCallback(() => {
        setIsOpen(true);
        userInteractedRef.current = true;
    }, []);

    const onAccommodationChanged = ({
        no_of_bedrooms_from,
        no_of_beds_from,
    }: {
        no_of_bedrooms_from?: number;
        no_of_beds_from?: number;
    }) => {
        if (no_of_bedrooms_from !== undefined) setBedrooms(no_of_bedrooms_from);
        if (no_of_beds_from !== undefined) setBeds(no_of_beds_from);
    };

    const bedroomText = formatGuestAndBedroomText(beds, bedrooms);

    if (!isOpen) return <SearchDetails filters={filters} openSearchForm={openSearchForm} />;

    return (
        <form
            action="/reise/feriehus-hytteutleie/resultat/"
            method="GET"
            key="fhhSearchForm"
            className="s-border border-b md:border md:rounded-8 my-16">
            <HiddenSearchInputs
                destinationData={destination}
                startDate={fromDate}
                endDate={toDate}
                numOfBeds={beds}
                numOfBedrooms={bedrooms}
                filters={filters}
            />
            <div className="flex flex-col md:flex-row gap-12 items-end p-16 md:p-32" data-testid="fhhNewSearch">
                <div className="w-full md:flex-1">
                    <Autocomplete
                        vertical="fhh"
                        id="newSearchDestination"
                        label="Destinasjon"
                        placeholder="Hvor som helst"
                        defaultValue={formatDestinationText(destination)}
                        url={`${apiExternalUrl}/autocomplete?query=`}
                        headers={{ 'x-gtoken': googlePlacesSessionToken }}
                        mapOptions={fhhAutocompleteMapper}
                        onSelect={(option) => setDestination(option)}
                    />
                </div>
                <div className="w-full md:flex-2">
                    <FrontpageDatePickerWrapper
                        startDate={fromDate}
                        endDate={toDate}
                        useRange
                        onDateChange={({ startDate, endDate }) => {
                            setFromDate(startDate);
                            setToDate(endDate);
                        }}>
                        <TextField id="newSearchCheckin" label="Innsjekk" />
                        <TextField id="newSearchCheckout" label="Utsjekk" />
                    </FrontpageDatePickerWrapper>
                </div>
                <div className="w-full md:flex-1">
                    <BedroomFilter
                        data-testid="fhhBedroomFilter"
                        trigger={{
                            text: bedroomText,
                            label: 'Reisende',
                        }}
                        filters={{ no_of_beds_from: beds, no_of_bedrooms_from: bedrooms }}
                        onFilterChanged={onAccommodationChanged}
                    />
                </div>
                <div className="w-full mt-8 md:flex-1 flex items-center gap-16">
                    <Button secondary fullWidth className="md:hidden" onClick={closeSearchForm}>
                        Lukk
                    </Button>
                    <Button primary fullWidth className="md:flex-1" type="submit">
                        Søk
                    </Button>
                </div>
            </div>
        </form>
    );
};
